import {
  AnnounceRecording,
  CallRecordingStatus,
  ConversationTrait,
} from '@api/generated/graphql';
import { PcsConversationType } from './lib/getPcsConversation';
export type { PcsConversationType };

export type CallStateDirection = 'originating' | 'terminating' | '';

export type PhoneConnectionCancelledEvent = {
  sessionSid: string;
  reason: 'timeout' | 'error' | 'user-cancelled';
};

export type WebphoneCallState = {
  connected: boolean;
  callId: string;
  direction: CallStateDirection;
  ended: boolean;
  established: boolean;
  hasSession: boolean;
  held: boolean;
  inTransfer: boolean;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  localIdentity: string;
  originating: boolean;
  primary: boolean;
  progress: boolean;
  remoteIdentity: string;
  terminating: boolean;
  registrationFailed: boolean;
  internalCall: boolean;
  remoteCallId: string;
  lastTriggeringEvent: string;
};

enum ExtendedRecordingStatus {
  Unknown = 'Unknown',
}

export const RecordingStatus = {
  ...CallRecordingStatus,
  ...ExtendedRecordingStatus,
};

export type CallRecordingStatusExtended =
  | CallRecordingStatus
  | ExtendedRecordingStatus;

export type RecordingState = {
  status: CallRecordingStatus | CallRecordingStatusExtended;
  disabled: boolean;
  announceRecording: AnnounceRecording;
};

export type CallAttributes = {
  pcsConversationSid: string;
  queueIds: string[];
  recordingState: RecordingState;
  tenantId: string;
  traits: ConversationTrait[];
};

export type CallEvent = WebphoneCallState & CallAttributes;

export type PcsConversationFetchedEvent = {
  pcsConversation: PcsConversationType;
  phoneStateUpdateEvent: WebphonePhoneStateEvent;
};

export type DialerState = {
  actionDisplay: string;
  actionColor: string;
  dialedNumbers: string;
};

export type PhoneState = {
  phoneConnected: boolean;
  connectionStatus: WebphoneConnectionStatus;
  registrationFailed: boolean;
  callList: WebphoneCallState[];
  callAttributes: {
    [callId: string]: CallAttributes;
  };
  registrationOverwritten: boolean;
  callsAccepting: string[];
  disconnectTriggeredLocally: boolean;
  debugInfo: {
    [key: string]: any;
  };
  existingRegistrationsDetected: boolean;
  isUnactivatedImpersonator: boolean;
  isRecovering: boolean;
  legacyJwtFetchStatus: LegacyJwtFetchStatus;
};

export type SoftPhoneCallControl = {
  answer: (callId?: string) => void;
  cancel: (callId: string) => void;
  hangup: (callId?: string) => void;
  hold: () => void;
  mute: () => void;
  unhold: (callId?: string) => void;
  unmute: () => void;
  transfer: (number: string) => void;
};

export type SoftPhoneDialer = {
  dial: (number: string | number) => void;
  clear: () => void;
  backspace: () => void;
  autoAction: () => void;
};

export type PhonePrepareOutgoingEvent = {
  outgoingNumber: string;
  displayName?: string;
  tenantId?: string;
  taskAttributes?: Record<string, any>;
};

export type WebphonePhoneStateEvent = {
  triggeringEvent: string;
  phoneConnected: boolean;
  connectionStatus: WebphoneConnectionStatus;
  registrationFailed: boolean;
  callState: WebphoneCallState;
  callList: WebphoneCallState[];
};

export type WebphoneCallEvent = {
  callId: string;
  cause: string;
  phoneNumber: string;
  reasonPhrase: string;
  eventType: string;
  originator: string;
  sipResponse: {
    reasonPhrase: string;
    statusCode: number;
  };
};

export type WebphoneCallEventPayloadEventDetails = {
  callId: string;
  phoneNumber: string;
  reasonPhrase: string;
  cause: string;
  originator: string;
  sipResponse: {
    reasonPhrase: string;
    statusCode: number;
  };
};

export type WebphoneCallEventPayload = {
  event: WebphoneCallEventPayloadEventDetails;
  eventType: string;
};

export type WebphoneConnectionInfoEvent = {
  connectionDetails: {
    userAgentConnected: boolean;
    userAgentIsReady: boolean;
    userAgentResgistered: boolean;
    userAgentStarted: boolean;
    webphoneConnected: boolean;
    webphoneRegistrationReportedFailed: boolean;
    websocketOptionsPingLatencyMs: number;
    websocketOptionsPingStatus: string;
  };
  connectionStatus: string;
};

export type SoftPhoneFeatureToggles = {
  enableCallMonitoring: boolean;
  preventDuplicateWebphoneRegistrations: boolean;
  enableServerConnectionMonitoring: boolean;
  enableAutomaticConnectionRecovery: boolean;
};

export type WebphoneUserContext = {
  pcsUserId: string;
  pcsAccountId: string;
  pcsContactCenterId: string;
};

/**
 * Webphone-specific configuration passed to the MFE
 */
export type WebphoneConfig = {
  jwt?: string; // Legacy: SIP JWT (when feature-rtc-webphone-handles-fetching-jwt is disabled)
  sipUri?: string; // Legacy: SIP URI (when feature-rtc-webphone-handles-fetching-jwt is disabled)
  hostHandlesWindowUnload?: boolean;
  debugLogging: boolean;
  pcsJwt: string;
  pcsApiUrl: string | undefined;
};

export type OutboundCallerIdInput = {
  name: string;
  number: string;
  tenantId: string | null;
};

export type RegistrationInfoEvent = {
  collectionTime: string;
  totalRegistrations: number;
  webphoneRegistrations: number;
  registrationTypesPresent: string[];
  registrations: SoftphoneRegistration[];
};

type SoftphoneRegistration = {
  authorizingId: string;
  authorizingType: string;
  username: string;
  userAgent: string;
  userId: string;
  firstRegistration: boolean;
  initialRegistration: string;
  lastRegistration: string;
  name: string;
  enabled: boolean;
  deviceType: string;
  ownerId: string;
};

export enum WebphoneConnectionStatus {
  Uninitialized = 'uninitialized',
  WaitingToConnect = 'waitingToConnect',
  Connecting = 'connecting',
  Connected = 'connected',
  Disconnected = 'disconnected',
  RegistrationFailed = 'registrationFailed',
  RegistrationOverwritten = 'registrationOverwritten',
  UserAgentNotStarted = 'userAgentNotStarted',
  UserAgentNotConnected = 'userAgentNotConnected',
  UserAgentNotRegistered = 'userAgentNotRegistered',
  UserAgentNotReady = 'userAgentNotReady',
  WebsocketOptionsPingWaiting = 'websocketOptionsPingWaiting',
  WebsocketOptionsPingFailed = 'websocketOptionsPingFailed',
  WebsocketOptionsPingTimeout = 'websocketOptionsPingTimeout',
  WebphoneJwtFetching = 'webphoneJwtFetching',
  WebphoneJwtFetchFailed = 'webphoneJwtFetchFailed',
  WebphoneJwtNotAvailable = 'webphoneJwtNotAvailable',
}

export enum WebphoneUnloadWarningOptions {
  Always = 'Always',
  Never = 'Never',
  OnActiveCallOnly = 'OnActiveCallOnly',
}

export enum LegacyJwtFetchStatus {
  Idle = 'idle',
  Fetching = 'fetching',
  Failed = 'failed',
}

export enum ConnectionStatusType {
  ConnectionProblem = 'connectionProblem',
  PersistentIssuesDetected = 'persistentIssuesDetected',
  RecoveryStarted = 'recoveryStarted',
  RecoverySuccess = 'recoverySuccess',
  RecoveryFailed = 'recoveryFailed',
  WebsocketDisconnected = 'websocketDisconnected',
  ExcessiveDisconnections = 'excessiveDisconnections',
}

/**
 * Represents a single audio input or output device exposed to the host application.
 */
export interface AudioDevice {
  readonly deviceId: string;
  readonly label: string;
}

/**
 * Categorized an error type for device failure events.
 * Maps from W3C DOMException.name values.
 */
export type ErrorCategory =
  | 'permission_denied'
  | 'device_not_found'
  | 'device_in_use'
  | 'media_api_unavailable'
  | 'other';

/** Shared fields for all device switch results */
interface DeviceSwitchResultBase {
  deviceId: string;
  previousDeviceId: string;
}

/** Device was successfully switched to a new device */
export interface DeviceSwitched extends DeviceSwitchResultBase {
  status: 'changed';
}

/** Device switch was requested but the same device is already active */
export interface DeviceUnchanged extends DeviceSwitchResultBase {
  status: 'unchanged';
}

/** Result of a device switch attempt */
export type DeviceSwitchResult = DeviceSwitched | DeviceUnchanged;

/** Shared fields for all device failure results */
interface DeviceFailureResultBase {
  previousDeviceId: string;
  requestedDeviceId: string;
  currentDeviceId: string;
}

/** Switch was attempted but device stayed the same */
export interface DeviceFailureUnchanged extends DeviceFailureResultBase {
  status: 'unchanged';
}

/** Switch failed but original device was successfully restored */
export interface DeviceFailureRolledBack extends DeviceFailureResultBase {
  status: 'rolled_back';
}

/** Switch failed and the device could not be restored */
export interface DeviceFailureSwitchFailed extends DeviceFailureResultBase {
  status: 'switch_failed';
  switchError: string;
  errorCategory: ErrorCategory;
}

/** Rollback after a failed switch also failed */
export interface DeviceFailureRollbackFailed extends DeviceFailureResultBase {
  status: 'rollback_failed';
  rollbackError: string;
  errorCategory: ErrorCategory;
}

/** Result of a failed audio device switch attempt */
export type DeviceFailureResult =
  | DeviceFailureUnchanged
  | DeviceFailureRolledBack
  | DeviceFailureSwitchFailed
  | DeviceFailureRollbackFailed;

/**
 * Payload for the devicesReady event emitted when audio devices are
 * enumerated and ready for use.
 */
export interface DevicesReadyPayload {
  readonly audioInputDevices: readonly AudioDevice[];
  readonly audioOutputDevices: readonly AudioDevice[];
  readonly activeAudioInputDeviceId: string;
  readonly activeAudioOutputDeviceId: string;
  readonly activeRingOutputDeviceId: string;
  readonly microphoneAnalyser?: AnalyserNode;
}

/**
 * Payload for the deviceListChanged event emitted when the list of
 * available audio devices changes (e.g. a device is plugged/unplugged).
 */
export interface DeviceListChangedPayload {
  readonly audioInputDevices: readonly AudioDevice[];
  readonly audioOutputDevices: readonly AudioDevice[];
  readonly activeAudioInputDeviceId: string;
  readonly activeAudioOutputDeviceId: string;
  readonly activeRingOutputDeviceId: string;
}

/**
 * Payload for the audioDevicesChanged event emitted when the active
 * audio devices change after a setAudioDevices command.
 */
export interface AudioDevicesChangedPayload {
  readonly audioInput: DeviceSwitchResult;
  readonly audioOutput: DeviceSwitchResult;
  readonly ringOutput: DeviceSwitchResult;
}

/**
 * Payload for the setAudioDevicesFailed event emitted when a
 * setAudioDevices command fails for one or more device kinds.
 */
export interface SetAudioDevicesFailedPayload {
  readonly audioInput: DeviceFailureResult;
  readonly audioOutput: DeviceFailureResult;
  readonly ringOutput: DeviceFailureResult;
}

/**
 * Payload for the deviceInitializationFailed event emitted when
 * the device manager cannot initialize audio devices on startup.
 */
export interface DeviceInitFailedPayload {
  readonly errorCategory: ErrorCategory;
  readonly errorName: string;
  readonly errorMessage: string;
}

/**
 * Payload for the micMonitorStarted event emitted when the microphone
 * monitor starts successfully.
 */
export interface MicMonitorStartedPayload {
  readonly loopback: boolean;
}

/**
 * Payload for the micMonitorFailed event emitted when the microphone
 * monitor fails to start.
 */
export interface MicMonitorFailedPayload {
  readonly errorCategory: ErrorCategory;
  readonly errorName: string;
  readonly errorMessage: string;
}

export interface ConnectionStatusUpdatePayload {
  status: ConnectionStatusType;
  timestamp: string;
  details?: {
    connectionStatus?: WebphoneConnectionStatus;
    lastServerOptionsPingTimestamp?: string | null;
    lastReregistrationTimestamp?: string | null;
    problemCount?: number;
    timeWindowMs?: number;
    problemTimestamps?: string[];
    lastProblem?: ConnectionStatusUpdatePayload;
    reason?: string;
    attemptNumber?: number;
    error?: string;
    errorDetails?: Record<string, any>;
    wasClean?: boolean;
    code?: number;
    disconnectionCount?: number;
    disconnectionTimestamps?: string[];
    lastDisconnection?: ConnectionStatusUpdatePayload;
  };
}
